import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { trackPromise } from 'react-promise-tracker';
import { FaUpload, FaTrashAlt, FaTimes, FaFileCode, FaExclamationTriangle, FaCheckCircle, FaInfoCircle, FaSearch } from 'react-icons/fa';
import { DEBUG, SOFTDATANODEJS } from '../../config';

const BOTablesDashboard = () => {
    // ==========================================
    // ESTADO CENTRALIZADO: CATEGORIAS (COLEÇÕES)
    // ==========================================
    const [allCategories, setAllCategories] = useState([]);

    const fetchAllCategories = useCallback(async () => {
        try {
            const res = await axios.get(`${SOFTDATANODEJS}/mongodb/opcoes/study-types`);
            setAllCategories(res.data);
        } catch (err) {            
            openNotify("Falha de Comunicação", "Não foi possível carregar as categorias da base de dados. Verifique a ligação ao servidor MongoDB.", "error");
        }
    }, []);

    // Carrega as categorias logo ao iniciar o Dashboard
   useEffect(() => {
        fetchAllCategories();
    }, [fetchAllCategories]);

    // ==========================================
    // STATES: MODAIS DE AÇÃO
    // ==========================================
    const [showImportModal, setShowImportModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showRemoveModal, setShowRemoveModal] = useState(false);

    // ==========================================
    // ESTADO: MODAL DE NOTIFICAÇÃO GENÉRICA (Substitui Alerts/Confirms)
    // ==========================================
    const [notifyConfig, setNotifyConfig] = useState({
        show: false,
        title: '',
        message: '',
        type: 'info', // 'info', 'success', 'error', 'confirm'
        onConfirm: null // Função a executar se for tipo 'confirm' e clicar 'Sim'
    });

    const openNotify = (title, message, type = 'info', onConfirm = null) => {
        setNotifyConfig({ show: true, title, message, type, onConfirm });
    };

    const closeNotify = () => {
        setNotifyConfig({ show: false, title: '', message: '', type: 'info', onConfirm: null });
    };

    const renderNotifyIcon = () => {
        switch (notifyConfig.type) {
            case 'error': return <FaExclamationTriangle style={{ color: '#dc3545', fontSize: '30px' }} />;
            case 'success': return <FaCheckCircle style={{ color: '#28a745', fontSize: '30px' }} />;
            case 'confirm': return <FaExclamationTriangle style={{ color: '#ffc107', fontSize: '30px' }} />;
            default: return <FaInfoCircle style={{ color: '#17a2b8', fontSize: '30px' }} />;
        }
    };

    // ==========================================
    // STATES & LÓGICA: IMPORTAR ESTUDO
    // ==========================================
    const [collection, setCollection] = useState('');
    const [isNewCollection, setIsNewCollection] = useState(false);
    const [dataFile, setDataFile] = useState(null);
    const [aggFile, setAggFile] = useState(null);
    const [importLog, setImportLog] = useState(null);

    const readFileAsText = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = (e) => reject(e);
            reader.readAsText(file);
        });
    };

    const processNormalization = (studyData, aggData) => {
        let corrections = 0;
        const aggMap = {};
        if (aggData.aggregators) {
            aggData.aggregators.forEach(agg => {
                aggMap[agg.IDAggregator] = agg.listValues.map(lv => {
                    const parts = String(lv.aggregatorValue).split('|');
                    return { parts, default: parts[0] };
                });
            });
        }
        const getDefault = (aggId, val) => {
            if (!aggMap[aggId]) return null;
            const strVal = String(val);
            for (const lv of aggMap[aggId]) { if (lv.parts.includes(strVal)) return lv.default; }
            return null;
        };

        const newStudy = { ...studyData };
        if (newStudy.answers) {
            newStudy.answers = newStudy.answers.map(ans => {
                const newAns = { ...ans };
                const aggList = newAns.aggregator || [];
                const metaList = Array.isArray(newAns.meta) ? newAns.meta : [newAns.meta];

                if (aggList.length > 0) {
                    const colAggId = aggList[0];
                    let colProp = metaList[0];
                    let rowAggId = null;
                    let rowProp = null;

                    if (aggList.length > 1) { rowAggId = aggList[1]; rowProp = metaList[0]; colProp = metaList[1]; }

                    if (newAns.values) {
                        newAns.values = newAns.values.map(valItem => {
                            const newItem = { ...valItem };
                            if (colAggId && !rowAggId) {
                                if (Array.isArray(newItem[colProp])) {
                                    const originalLen = newItem[colProp].length;
                                    newItem[colProp] = newItem[colProp].map(v => {
                                        const def = getDefault(colAggId, v);
                                        return def !== null ? Number(def) : v;
                                    });
                                    newItem[colProp] = [...new Set(newItem[colProp])];
                                    if (originalLen !== newItem[colProp].length) corrections++;
                                }
                            } else if (colAggId && rowAggId) {
                                if (newItem.value && Array.isArray(newItem.value)) {
                                    let groupedLines = {};
                                    newItem.value.forEach(vObj => {
                                        let lineVal = vObj[rowProp];
                                        const lineDef = getDefault(rowAggId, lineVal);
                                        if (lineDef !== null) lineVal = Number(lineDef);

                                        let colVals = vObj[colProp];
                                        if (Array.isArray(colVals)) { colVals = colVals.map(cv => { const cDef = getDefault(colAggId, cv); return cDef !== null ? Number(cDef) : cv; }); }

                                        if (!groupedLines[lineVal]) groupedLines[lineVal] = [];
                                        if (Array.isArray(colVals)) { groupedLines[lineVal] = groupedLines[lineVal].concat(colVals); } 
                                        else { groupedLines[lineVal].push(colVals); }
                                    });

                                    const oldLen = newItem.value.length;
                                    newItem.value = Object.keys(groupedLines).map(k => ({ [rowProp]: Number(k), [colProp]: [...new Set(groupedLines[k])] }));
                                    if (oldLen !== newItem.value.length) corrections++;
                                }
                            }
                            return newItem;
                        });
                    }
                }
                return newAns;
            });
        }
        return { normalizedData: newStudy, corrections };
    };

    const handleImportSubmit = async () => {
        if (!collection || !dataFile || !aggFile) {
            return openNotify("Campos Obrigatórios", "Por favor, preencha o tipo de estudo e escolha ambos os ficheiros JSON.", "error");
        }
        setImportLog(null);
        try {
            const dataText = await readFileAsText(dataFile);
            const aggText = await readFileAsText(aggFile);
            const studyData = JSON.parse(dataText);
            const aggData = JSON.parse(aggText);

            const { normalizedData, corrections } = processNormalization(studyData, aggData);

            const jsonString = JSON.stringify(normalizedData);
            const normalizedBlob = new Blob([jsonString], { type: "application/json" });
            const normalizedFile = new File([normalizedBlob], `normalized_${dataFile.name}`, { type: "application/json" });

            const formData = new FormData();
            formData.append('tipoEstudo', collection);
            formData.append('file', normalizedFile);
            formData.append('aggregatorFile', aggFile);

            await axios.post(`${SOFTDATANODEJS}/tables/import`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            setImportLog(`Sucesso! ${corrections} blocos corrigidos e importados na BD.`);
            
            // ATUALIZA A LISTA GLOBAL DE COLEÇÕES APÓS IMPORTAÇÃO
            fetchAllCategories();

            setCollection('');
            setDataFile(null);
            setAggFile(null);
            document.getElementById('dataFile').value = '';
            document.getElementById('aggFile').value = '';
        } catch (error) {
            if (DEBUG){
                console.error("Erro completo:", error);
                console.error("Response:", error.response);
                console.error("Response data:", error.response?.data);
            }
            let mensagemErro =
                error.response?.data?.error ||
                error.response?.data?.message;

            if (!mensagemErro && typeof error.response?.data === "string") {
                try {
                    const parsed = JSON.parse(error.response.data);
                    mensagemErro = parsed.error || parsed.message;
                } catch {
                    mensagemErro = error.response.data;
                }
            }

            if (!mensagemErro) {
                mensagemErro = "Ocorreu um erro ao processar ou importar os ficheiros JSON. Verifique se os formatos estão corretos.";
            }

            openNotify("Erro na Importação", mensagemErro, "error");
        }
    };

    const closeImportModal = () => {
        setShowImportModal(false);
        setIsNewCollection(false);
        setImportLog(null); setCollection(''); setDataFile(null); setAggFile(null);
    };

    // ==========================================
    // STATES & LÓGICA: CONSULTAR VAGAS
    // ==========================================
    const [selectedViewCategory, setSelectedViewCategory] = useState('');
    const [viewWaves, setViewWaves] = useState([]);

    useEffect(() => {
        if (!selectedViewCategory) {
            setViewWaves([]); return;
        }
        trackPromise(
            axios.get(`${SOFTDATANODEJS}/mongodb/opcoes/study-waves/${selectedViewCategory}`)
                .then(res => setViewWaves(res.data))
                .catch(err => {
                    openNotify("Erro de Vagas", `Não foi possível carregar a lista de vagas para o estudo ${selectedViewCategory}.`, "error");
                    setViewWaves([]);
                })
        );
    }, [selectedViewCategory]);

    const closeViewModal = () => {
        setShowViewModal(false);
        setSelectedViewCategory(''); setViewWaves([]);
    };

    // ==========================================
    // STATES & LÓGICA: REMOVER VAGA / ESTUDO
    // ==========================================
    const [waves, setWaves] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedWave, setSelectedWave] = useState('');

    useEffect(() => {
        if (!selectedCategory) {
            setWaves([]); setSelectedWave(''); return;
        }
        trackPromise(
            axios.get(`${SOFTDATANODEJS}/mongodb/opcoes/study-waves/${selectedCategory}`)
                .then(res => setWaves(res.data))
                .catch(err => {
                    openNotify("Erro de Vagas", `Não foi possível carregar a lista de vagas para o estudo ${selectedCategory}.`, "error");
                    setWaves([]);
                })
        );
    }, [selectedCategory]);

    const performDelete = async () => {
        closeNotify(); 
        const isDeleteAll = selectedWave === 'ALL';
        
        try {
            if (isDeleteAll) {
                // Rota para apagar a coleção inteira
                await trackPromise(axios.delete(`${SOFTDATANODEJS}/tables/study/${selectedCategory}`));
                openNotify("Sucesso", `O estudo "${selectedCategory}" e os respetivos agregadores foram totalmente eliminados do sistema.`, "success");
                
                // ATUALIZA A LISTA GLOBAL DE CATEGORIAS APÓS ELIMINAR COLEÇÃO
                fetchAllCategories();
                
                setSelectedCategory('');
                setSelectedWave('');
                setWaves([]);
            } else {
                // Rota para apagar apenas a Vaga
                await trackPromise(axios.delete(`${SOFTDATANODEJS}/tables/study/${selectedCategory}/wave/${selectedWave}`));
                openNotify("Sucesso", `A vaga "${selectedWave}" do estudo "${selectedCategory}" foi eliminada com sucesso.`, "success");
                
                // Atualiza apenas as vagas da categoria selecionada
                setSelectedWave('');
                const res = await trackPromise(axios.get(`${SOFTDATANODEJS}/mongodb/opcoes/study-waves/${selectedCategory}`));
                setWaves(res.data);
            }
        } catch (error) {
            openNotify("Erro ao Eliminar", "Não foi possível eliminar os dados selecionados. Por favor tente novamente.", "error");
        }
    };

    const handleDeleteSubmit = () => {
        if (!selectedCategory || !selectedWave) return;
        
        const isDeleteAll = selectedWave === 'ALL';
        const msg = isDeleteAll 
            ? `Tem a certeza absoluta que deseja eliminar PERMANENTEMENTE TODO o estudo "${selectedCategory}"? Esta ação apagará a coleção inteira e os seus agregadores, não sendo possível reverter.`
            : `Tem a certeza absoluta que deseja eliminar PERMANENTEMENTE a vaga "${selectedWave}" do estudo "${selectedCategory}" do MongoDB?`;

        openNotify("Confirmar Eliminação", msg, "confirm", performDelete);
    };

    const closeRemoveModal = () => {
        setShowRemoveModal(false);
        setSelectedCategory(''); setSelectedWave(''); setWaves([]);
    };

    // ==========================================
    // RENDER: DASHBOARD E MODAIS
    // ==========================================
    return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            
            {/* PAINEL CENTRAL (Botões) */}
            <div style={{ display: 'flex', gap: '30px', marginTop: '40px', flexWrap: 'wrap', justifyContent: 'center' }}>
                <div style={styles.cardBtn} onClick={() => setShowImportModal(true)}>
                    <FaUpload style={{ fontSize: '50px', color: '#1F4E64', marginBottom: '15px' }} />
                    <h3 style={{ margin: 0, color: '#333' }}>Importar Estudo</h3>
                    <p style={{ fontSize: '13px', color: '#666', textAlign: 'center', marginTop: '10px' }}>
                        Normaliza e importa ficheiros JSON (Dados e Agregadores) para o MongoDB.
                    </p>
                </div>

                <div style={styles.cardBtn} onClick={() => setShowViewModal(true)}>
                    <FaSearch style={{ fontSize: '50px', color: '#28a745', marginBottom: '15px' }} />
                    <h3 style={{ margin: 0, color: '#333' }}>Consultar Vagas</h3>
                    <p style={{ fontSize: '13px', color: '#666', textAlign: 'center', marginTop: '10px' }}>
                        Verifica as coleções e respetivas vagas que já se encontram ativas na BD.
                    </p>
                </div>

                <div style={styles.cardBtn} onClick={() => setShowRemoveModal(true)}>
                    <FaTrashAlt style={{ fontSize: '50px', color: '#dc3545', marginBottom: '15px' }} />
                    <h3 style={{ margin: 0, color: '#333' }}>Remover Vaga / Estudo</h3>
                    <p style={{ fontSize: '13px', color: '#666', textAlign: 'center', marginTop: '10px' }}>
                        Elimina uma vaga específica ou limpa um estudo inteiro do sistema.
                    </p>
                </div>
            </div>

            {/* MODAL: IMPORTAR ESTUDO */}
            {showImportModal && (
                <div style={styles.modalOverlay}>
                    <div style={{ ...styles.modalContent, maxWidth: '600px' }}>
                        <div style={styles.modalHeader}>
                            <h2 style={styles.modalTitle}>Importar Estudo (JSON)</h2>
                            <button style={styles.closeBtn} onClick={closeImportModal}><FaTimes /></button>
                        </div>
                        
                        <div style={{ padding: '15px' }}>
                            <table style={styles.infoTable}>
                                <tbody>
                                    <tr>
                                        <td style={styles.infoLabel}>Coleção MongoDB:</td>
                                        <td style={styles.infoVal}>
                                            {!isNewCollection ? (
                                                <div style={{ display: 'flex', gap: '10px' }}>
                                                    <select 
                                                        value={collection} 
                                                        onChange={(e) => {
                                                            if (e.target.value === 'NEW') {
                                                                setIsNewCollection(true);
                                                                setCollection('');
                                                            } else {
                                                                setCollection(e.target.value);
                                                            }
                                                        }} 
                                                        style={styles.modalInput}
                                                    >
                                                        <option value="">-- selecione uma coleção --</option>
                                                        <option value="NEW" style={{fontWeight: 'bold', color: '#1F4E64'}}>+ CRIAR NOVA COLEÇÃO</option>
                                                        {/* Usa a lista que já confirmámos que chega bem! */}
                                                        {allCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                                    </select>
                                                </div>
                                            ) : (
                                                <div style={{ display: 'flex', gap: '10px' }}>
                                                    <input 
                                                        type="text" 
                                                        value={collection} 
                                                        onChange={(e) => setCollection(e.target.value)} 
                                                        style={styles.modalInput} 
                                                        placeholder="Escreva o nome da nova coleção..." 
                                                    />
                                                    <button 
                                                        onClick={() => { setIsNewCollection(false); setCollection(''); }}
                                                        style={{ padding: '8px 12px', cursor: 'pointer', background: '#e1e1e1', border: '1px solid #ccc', borderRadius: '4px', fontSize: '12px' }}
                                                        title="Voltar à lista"
                                                    >
                                                        Cancelar
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={styles.infoLabel}>Ficheiro de Dados:</td>
                                        <td style={styles.infoVal}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <FaFileCode style={{ color: '#005c75', fontSize: '20px' }} />
                                                <input id="dataFile" type="file" accept=".json" onChange={(e) => setDataFile(e.target.files[0])} style={{ fontSize: '12px', color: '#333', width: '100%' }} />
                                            </div>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={styles.infoLabel}>Agregadores:</td>
                                        <td style={styles.infoVal}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <FaFileCode style={{ color: '#E67E22', fontSize: '20px' }} />
                                                <input id="aggFile" type="file" accept=".json" onChange={(e) => setAggFile(e.target.files[0])} style={{ fontSize: '12px', color: '#333', width: '100%' }} />
                                            </div>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            {importLog && (
                                <div style={{ marginTop: '15px', padding: '10px', backgroundColor: '#d4edda', border: '1px solid #c3e6cb', borderRadius: '4px', color: '#155724', fontSize: '13px', textAlign: 'center', fontWeight: 'bold' }}>
                                    {importLog}
                                </div>
                            )}
                        </div>

                        <div style={styles.modalFooter}>
                            <button style={{ ...styles.footerBtn, background: '#1F4E64', color: 'white' }} disabled={!collection || !dataFile || !aggFile} onClick={handleImportSubmit}>
                                Processar e Importar
                            </button>
                            <button style={styles.footerBtn} onClick={closeImportModal}>Cancelar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL: CONSULTAR VAGAS */}
            {showViewModal && (
                <div style={styles.modalOverlay}>
                    <div style={{ ...styles.modalContent, maxWidth: '500px' }}>
                        <div style={styles.modalHeader}>
                            <h2 style={{ ...styles.modalTitle, color: '#28a745' }}>Consultar Estudos / Vagas</h2>
                            <button style={styles.closeBtn} onClick={closeViewModal}><FaTimes /></button>
                        </div>

                        <div style={{ padding: '15px' }}>
                            <table style={styles.infoTable}>
                                <tbody>
                                    <tr>
                                        <td style={styles.infoLabel}>Categoria / Estudo:</td>
                                        <td style={styles.infoVal}>
                                            <select value={selectedViewCategory} onChange={(e) => setSelectedViewCategory(e.target.value)} style={styles.modalInput}>
                                                <option value="">-- selecione um estudo --</option>
                                                {/* USA O ESTADO CENTRALIZADO */}
                                                {allCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                            </select>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            {selectedViewCategory && (
                                <div style={{ marginTop: '20px' }}>
                                    <h4 style={{ color: '#333', margin: '0 0 10px 0', fontSize: '14px' }}>
                                        Vagas Disponíveis ({viewWaves.length}):
                                    </h4>
                                    <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid #ccc', borderRadius: '4px', backgroundColor: '#f9f9f9', padding: '15px' }}>
                                        {viewWaves.length > 0 ? (
                                            <ul style={{ margin: 0, paddingLeft: '20px', color: '#1F4E64', fontSize: '13px', fontWeight: 'bold', lineHeight: '1.8' }}>
                                                {viewWaves.map((wave, index) => (
                                                    <li key={index}>{wave}</li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <span style={{ fontSize: '13px', color: '#666' }}>Nenhuma vaga encontrada para este estudo.</span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div style={styles.modalFooter}>
                            <button style={{ ...styles.footerBtn, background: '#28a745', color: 'white', border: 'none' }} onClick={closeViewModal}>
                                Fechar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL: REMOVER VAGA / ESTUDO */}
            {showRemoveModal && (
                <div style={styles.modalOverlay}>
                    <div style={{ ...styles.modalContent, maxWidth: '500px' }}>
                        <div style={styles.modalHeader}>
                            <h2 style={{ ...styles.modalTitle, color: '#dc3545' }}>Remover Estudo / Vaga</h2>
                            <button style={styles.closeBtn} onClick={closeRemoveModal}><FaTimes /></button>
                        </div>

                        <div style={{ padding: '15px' }}>
                            <table style={styles.infoTable}>
                                <tbody>
                                    <tr>
                                        <td style={styles.infoLabel}>Categoria / Estudo:</td>
                                        <td style={styles.infoVal}>
                                            <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)} style={styles.modalInput}>
                                                <option value="">-- selecione um estudo --</option>
                                                {/* USA O ESTADO CENTRALIZADO */}
                                                {allCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                                            </select>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style={styles.infoLabel}>Vaga (Wave):</td>
                                        <td style={styles.infoVal}>
                                            <select value={selectedWave} onChange={(e) => setSelectedWave(e.target.value)} style={styles.modalInput} disabled={!selectedCategory}>
                                                <option value="">-- selecione uma vaga --</option>
                                                <option value="ALL" style={{ color: '#dc3545', fontWeight: 'bold' }}>⚠️ APAGAR ESTUDO COMPLETO</option>
                                                {waves.map(wave => <option key={wave} value={wave}>{wave}</option>)}
                                            </select>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                            
                            {selectedWave === 'ALL' ? (
                                <div style={{ marginTop: 15, background: '#f8d7da', padding: 10, borderRadius: 4, color: '#721c24', fontSize: '13px', border: '1px solid #f5c6cb' }}>
                                    <strong>Atenção:</strong> Escolheu apagar o <strong>Estudo Completo</strong>. Toda a coleção e dicionários no MongoDB serão eliminados.
                                </div>
                            ) : (
                                <div style={{ marginTop: 15, background: '#fff3cd', padding: 10, borderRadius: 4, color: '#856404', fontSize: '12px', border: '1px solid #ffeeba' }}>
                                    <strong>Aviso:</strong> A ação apagará permanentemente os registos MongoDB correspondentes à Vaga selecionada.
                                </div>
                            )}
                        </div>

                        <div style={styles.modalFooter}>
                            <button style={{ ...styles.footerBtn, background: (!selectedCategory || !selectedWave) ? '#ccc' : '#dc3545', color: 'white', border: 'none' }} disabled={!selectedCategory || !selectedWave} onClick={handleDeleteSubmit}>
                                Eliminar Definitivamente
                            </button>
                            <button style={styles.footerBtn} onClick={closeRemoveModal}>Cancelar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* ==========================================
                MODAL GENÉRICA: NOTIFICAÇÃO / CONFIRMAÇÃO 
                ========================================== */}
            {notifyConfig.show && (
                <div style={{ ...styles.modalOverlay, zIndex: 3000 }}>
                    <div style={{ ...styles.modalContent, maxWidth: '400px' }}>
                        <div style={{ ...styles.modalHeader, borderBottom: notifyConfig.type === 'error' ? '2px solid #dc3545' : notifyConfig.type === 'confirm' ? '2px solid #ffc107' : notifyConfig.type === 'success' ? '2px solid #28a745' : styles.modalHeader.borderBottom }}>
                            <h2 style={{ ...styles.modalTitle, color: notifyConfig.type === 'error' ? '#dc3545' : notifyConfig.type === 'confirm' ? '#856404' : notifyConfig.type === 'success' ? '#28a745' : styles.modalTitle.color }}>
                                {notifyConfig.title}
                            </h2>
                            <button style={styles.closeBtn} onClick={closeNotify}><FaTimes /></button>
                        </div>
                        
                        <div style={{ padding: '20px', display: 'flex', alignItems: 'center', gap: '20px' }}>
                            {renderNotifyIcon()}
                            <div style={{ fontSize: '14px', color: '#333', lineHeight: '1.5', flex: 1 }}>
                                {notifyConfig.message}
                            </div>
                        </div>

                        <div style={{ ...styles.modalFooter, backgroundColor: '#fefefe' }}>
                            {notifyConfig.type === 'confirm' ? (
                                <>
                                    <button style={{ ...styles.footerBtn, background: '#dc3545', color: 'white', border: '1px solid #dc3545' }} onClick={notifyConfig.onConfirm}>
                                        Sim, Eliminar
                                    </button>
                                    <button style={styles.footerBtn} onClick={closeNotify}>Cancelar</button>
                                </>
                            ) : (
                                <button style={{ ...styles.footerBtn, background: '#1F4E64', color: 'white', border: '1px solid #1F4E64' }} onClick={closeNotify}>
                                    OK
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ESTILOS UNIFICADOS
const styles = {
    cardBtn: { width: '250px', height: '200px', backgroundColor: '#fff', borderRadius: '8px', boxShadow: '0 4px 10px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: '20px', transition: 'transform 0.2s', boxSizing: 'border-box' },
    modalOverlay: { position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 2000 },
    modalContent: { backgroundColor: '#fff', width: '90%', borderRadius: '5px', boxShadow: '0 0 20px rgba(0,0,0,0.5)', display: 'flex', flexDirection: 'column', maxHeight: '90vh', boxSizing: 'border-box' },
    modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 15px 10px 15px', borderBottom: '2px solid #1F4E64' },
    modalTitle: { margin: 0, fontSize: '18px', color: '#1F4E64', fontWeight: 'bold' },
    closeBtn: { background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#666' },
    infoTable: { width: '100%', borderCollapse: 'collapse', border: '1px solid #ccc' },
    infoLabel: { width: '35%', backgroundColor: '#f2f2f2', color: '#333', fontWeight: 'bold', textAlign: 'right', padding: '10px', fontSize: '13px', border: '1px solid #ccc', boxSizing: 'border-box' },
    infoVal: { padding: '10px', fontSize: '13px', color: '#333', border: '1px solid #ccc', boxSizing: 'border-box' },
    modalInput: { width: '100%', padding: '8px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px', color: '#333', boxSizing: 'border-box', backgroundColor: '#fff' },
    modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '15px', backgroundColor: '#f9f9f9', borderTop: '1px solid #eee', borderRadius: '0 0 5px 5px', boxSizing: 'border-box' },
    footerBtn: { padding: '8px 20px', cursor: 'pointer', borderRadius: '4px', border: '1px solid #999', background: '#e1e1e1', fontSize: '13px', fontWeight: 'bold', boxSizing: 'border-box' }
};

export default BOTablesDashboard;